// react
import * as React from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';

// tanstack
import { useParams } from '@tanstack/react-router';
// tanstack
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';

// material ui
import { Box, CssBaseline, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// shared components
import type { NavigationContext } from '@/components/navigation';

// components
import { useAdminViewMode } from '@/contexts/AdminViewModeContext';
import { useCookiePreferences } from '@/contexts/CookiePreferencesContext';
import { useSessionAuth } from '@/contexts/SessionAuthContext';

// relative
import { getSelectedAccount } from '../redux/selectedAccountSlice';
import AppBar from './AppBar';
import MainContent from './MainContent';
import NavigationDrawer from './NavigationDrawer';

interface AppLayoutProps {
  children: ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const theme = useTheme();
  const { openCookiePreferences } = useCookiePreferences();

  const { isAdmin } = useSessionAuth();
  const { accountId } = useParams({ strict: false });

  const enabledAccountIds = ['8649', '9080'];
  const enableChatButton = Boolean(
    accountId && isAdmin && enabledAccountIds.includes(accountId),
  );

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));

  const [desktopOpen, setDesktopOpen] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { adminViewMode: isAdminViewModeActive } = useAdminViewMode();

  const selectedAccount = useSelector(getSelectedAccount);

  // Build services array from selectedAccount
  const services = selectedAccount?.Services
    ? selectedAccount.Services.map((service) => service.Name)
    : [];

  const navigationContext: NavigationContext = {
    isAdmin: isAdmin,
    services: services,
    adminViewMode: isAdminViewModeActive,
  };

  // Auto open/close the permanent drawer around the md breakpoint
  React.useEffect(() => {
    if (isMobile) return; // mobile uses temporary drawer
    setDesktopOpen(lgUp); // md+ open, below md collapsed
  }, [lgUp, isMobile]);

  const handleMenu = () => {
    if (isMobile) setMobileOpen(true);
    else setDesktopOpen((v) => !v);
  };

  const closeMobile = () => setMobileOpen(false);

  const frameBg =
    theme.palette.mode === 'light'
      ? theme.palette.grey[0]
      : theme.palette.grey[1000];

  const drawerBg =
    theme.palette.mode === 'light'
      ? theme.palette.grey[0]
      : theme.palette.grey[1000];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: frameBg }}>
      <TanStackRouterDevtools position="bottom-right" />

      <CssBaseline />

      <AppBar
        isMobile={isMobile}
        desktopOpen={desktopOpen}
        onMenuClick={handleMenu}
        frameBg={frameBg}
        enableChatButton={enableChatButton}
      />

      <NavigationDrawer
        isMobile={isMobile}
        desktopOpen={desktopOpen}
        mobileOpen={mobileOpen}
        onMobileClose={closeMobile}
        navigationContext={navigationContext}
        drawerBg={drawerBg}
        onOpenCookiePreferences={openCookiePreferences}
      />

      <MainContent isMobile={isMobile} frameBg={frameBg}>
        {children}
      </MainContent>
    </Box>
  );
}
